import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RATE_ROUNDING_DECIMALS } from '@efex-components/payables/PaymentModal/constants/catalogs';
import {
  UseRateValidationAlertParams,
  UseRateValidationAlertReturn,
} from './useRateValidationAlert.types';
import {
  AlertDisplayProps,
  RateValidationState,
} from '../RateValidationAlert.types';

export const useRateValidationAlert = ({
  validatePromoterRate,
  finalExchangeRateInput,
  hasUserInteractedWithRate,
  isCurrentRateValid,
  exchangeData,
  createLoading,
  createData,
  createError,
  originAmount,
  destinationAmount,
}: UseRateValidationAlertParams): UseRateValidationAlertReturn => {
  const { t } = useTranslation();

  const validationState: RateValidationState = useMemo(() => {
    let validationResult = null;

    if (finalExchangeRateInput && hasUserInteractedWithRate) {
      const rate = parseFloat(finalExchangeRateInput);
      if (!Number.isNaN(rate) && rate > 0) {
        validationResult = validatePromoterRate(rate, exchangeData);
      }
    }

    return {
      validationResult,
      hasUserInteraction: hasUserInteractedWithRate,
      rateInput: finalExchangeRateInput,
      exchangeData,
      isCreating: createLoading,
      createData,
      createError,
    };
  }, [
    validatePromoterRate,
    finalExchangeRateInput,
    hasUserInteractedWithRate,
    exchangeData,
    createLoading,
    createData,
    createError,
  ]);

  const alertProps: AlertDisplayProps = useMemo(() => {
    const {
      validationResult,
      hasUserInteraction,
      createData: data,
      createError: error,
      isCreating,
    } = validationState;

    if (error) {
      return {
        shouldShow: true,
        severity: 'error',
        message: `${t('error')}: ${error}`,
      };
    }

    if (
      !isCurrentRateValid &&
      finalExchangeRateInput &&
      hasUserInteraction &&
      validationResult
    ) {
      return {
        shouldShow: true,
        severity: 'error',
        message: t('rateMustBe', {
          comparison: t(validationResult.messageParams.comparison),
          rate: validationResult.messageParams.rate,
        }),
      };
    }

    if (data && !error && hasUserInteraction && isCurrentRateValid) {
      const hasOrigin = originAmount && originAmount !== '0';
      const hasDestination = destinationAmount && destinationAmount !== '0';

      if (hasOrigin || hasDestination) {
        const { isMainCurrency } = exchangeData || { isMainCurrency: false };
        const formattedValue = parseFloat(data.exchangeValue).toFixed(
          RATE_ROUNDING_DECIMALS
        );
        const correctLabel = isMainCurrency
          ? `1 ${data.currency} = ${formattedValue} ${data.desCurrency}`
          : `1 ${data.desCurrency} = ${formattedValue} ${data.currency}`;
        return {
          shouldShow: true,
          severity: 'success',
          message: `${t('rateSaved')}: ${correctLabel}`,
          showLoading: isCreating,
          loadingMessage: isCreating ? `${t('savingRate')}...` : undefined,
        };
      }
    }

    return {
      shouldShow: false,
      severity: 'info',
      message: '',
    };
  }, [
    validationState,
    t,
    isCurrentRateValid,
    originAmount,
    destinationAmount,
    finalExchangeRateInput,
    exchangeData,
  ]);

  return {
    alertProps,
    validationState,
  };
};

export default useRateValidationAlert;
